'use client'

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  themeBackgroundBlurMap,
  themeBackgroundMap,
  themeBorderColorMap,
  themeDivideColorMap,
} from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import { AnimatePresence, motion } from 'motion/react'
import { Fragment, forwardRef } from 'react'
import { PopoverListener } from '../popover/popover-listener.tsx'
import { MultiselectChips } from './multiselect-chips.tsx'
import { MultiselectPopoutOptions } from './multiselect-popout-options.tsx'
import { MultiselectSearch } from './multiselect-search.tsx'
import type {
  MultiselectPopoutInnerProps,
  MultiselectPopoutProps,
} from './types.ts'
import {
  MultiselectProvider,
  useMultiselect,
  useMultiselectContext,
} from './use-multiselect.ts'
const MultiselectPopoutInner = forwardRef<
  HTMLDivElement,
  MultiselectPopoutInnerProps
>(
  (
    {
      chipVariant,
      className,
      listLocation,
      placeholder,
      searchable,
      singleLine,
      ...useFormFieldProps
    },
    ref
  ) => {
    const { onClose } = useMultiselectContext()
    return (
      <Popover className={cn('w-full', className)}>
        {({ open }) => (
          <>
            <PopoverListener open={open} onClose={onClose} />
            <PopoverButton as={Fragment}>
              <MultiselectChips
                chipVariant={chipVariant}
                listLocation={listLocation ?? 'inside'}
                placeholder={placeholder ?? 'Select options'}
                ref={ref}
                singleLine={singleLine}
                {...useFormFieldProps}
              />
            </PopoverButton>
            <AnimatePresence>
              {open && (
                <PopoverPanel
                  static
                  as={motion.div}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  anchor={{
                    to: 'bottom start',
                    gap: '16px',
                    padding: '32px',
                  }}
                  className={cn(
                    'flex flex-col origin-top rounded-sm border divide-y focus-visible:outline-hidden min-w-[var(--button-width)] overflow-hidden z-30 shadow-lg',
                    themeBackgroundBlurMap['blur-xl'],
                    themeBackgroundMap['container-secondary'],
                    themeBorderColorMap.muted,
                    themeDivideColorMap.muted
                  )}
                >
                  {searchable && (
                    <MultiselectSearch placeholder={placeholder} />
                  )}
                  <MultiselectPopoutOptions />
                </PopoverPanel>
              )}
            </AnimatePresence>
          </>
        )}
      </Popover>
    )
  }
)

const MultiselectPopout = forwardRef<HTMLDivElement, MultiselectPopoutProps>(
  (
    {
      getPlusMoreText,
      selectedItems,
      onChange,
      onClose,
      options,
      ...innerProps
    },
    ref
  ) => {
    const context = useMultiselect({
      getPlusMoreText,
      isPopout: true,
      selectedItems,
      onChange,
      onClose,
      options,
      ...innerProps,
    })

    return (
      <MultiselectProvider value={context}>
        <MultiselectPopoutInner {...innerProps} ref={ref} />
      </MultiselectProvider>
    )
  }
)

export { MultiselectPopout }
